/**
 * entitlements — die eine Tier-Wahrheit im Frontend (Pricing-Repackaging PR-T3).
 *
 * Pure, mock-frei testbar — enthält KEINE Firebase-/API-Zugriffe. `resolveTier`
 * ist byte-semantisch identisch zur functions-Version `resolveEffectivePlan`
 * (`functions/src/tier/tierMapping.ts`) — beide implementieren dieselbe
 * Derivations-Regel aus `architecture-pricing-repackaging.md` §2.1. Die
 * kanonische I/O-Fixture-Tabelle aus §7 ist in allen Sprach-Resolvern
 * (functions/receipts/FE/PHP) identisch zu testen (Cross-Language-Vertrags-Muster,
 * §5.2 „Doppel-Repo-Drift"-Risiko).
 *
 * Inert-Kriterium PR-T3: Kein Gate konsumiert diesen Resolver — nur
 * `SubscriptionPage.tsx` nutzt `resolveTier` für die Anzeige des aktuellen
 * Plans (Tabelle selbst hinter `ULTRA_TIER_ENABLED`, live aus). `TIER_V2_FLAG`
 * ist live `false`/unset. Kein Nutzer hat heute `plan`/`grandfatheredFeatures`
 * gesetzt (Feld existiert erst ab T1/T2-GA) ⇒ kein Verhaltensdelta.
 *
 * Bewusst KEIN neues Feld am `AuthContext`/`Seller`-Typ (§3.2) — Call-Sites
 * berechnen `const tier = resolveTier(sellerData)` aus dem vorhandenen
 * `useAuth().sellerData`. Deshalb `SellerLike` als eigenständiges, loses
 * Struktur-Interface (statt Import von `Seller`): robust gegen fehlende Felder
 * auf dem heutigen `Seller`-Typ und gegen kaputte/undefinierte Roh-Firestore-Daten.
 *
 * @module entitlements
 */

/** Vollständiger Tier-Zustand eines Sellers (FE-Spiegel von `functions` `Plan`). */
export type Tier = 'free' | 'premium' | 'ultra';

/**
 * Tier-Leiter für Mindest-Tier-Vergleiche (`hasMinTier`). Höherer Wert = mehr
 * Berechtigung.
 */
export const TIER_ORDER: Record<Tier, number> = {
  free: 0,
  premium: 1,
  ultra: 2,
};

/** Kanonische, gültige `plan`-Werte (Reihenfolge = Tier-Leiter, wie functions-Pendant). */
const VALID_TIERS: readonly Tier[] = ['free', 'premium', 'ultra'];

/**
 * Loses Struktur-Interface für alles, was `resolveTier`/`isGrandfathered`
 * lesen können müssen. Absichtlich NICHT `Seller` importiert (§3.2) — jedes
 * Objekt mit (optionalen) `plan`/`isPremium`/`grandfatheredFeatures`-Feldern
 * erfüllt dieses Interface, inkl. kaputter/roher Firestore-Reads.
 */
export interface SellerLike {
  plan?: unknown;
  isPremium?: unknown;
  grandfatheredFeatures?: unknown;
  /**
   * NEU (Grace-Window, §4.1): Map `feature → ISO-8601-UTC-String`, EXKLUSIVES
   * Ablauf-Ende je Feature. Additiv-optional ⇒ kein Compile-Bruch bestehender
   * Call-Sites. Roh aus Firestore (`unknown`), fail-closed geparst über
   * `readGraceUntilMs`. Ausschließlich Backend-befüllt (Admin-Backfill), niemals
   * FE-geschrieben (Feld-Schutz M-G2).
   */
  grandfatheredGraceUntil?: unknown;
}

/**
 * Leitet den effektiven Tier eines Sellers ab (Derivations-Regel §2.1).
 *
 * Reihenfolge (fail-closed):
 *   1. gültiger `plan`-String (`'free'|'premium'|'ultra'`) → dieser Wert
 *   2. sonst `isPremium === true` → `'premium'`
 *   3. sonst → `'free'`
 *
 * Ein unbekannter/kaputter `plan`-String (z. B. Legacy `'kanzlei'`) fällt auf
 * die `isPremium`-Ableitung zurück — NIE auf `'ultra'`. Bestands-Docs ohne
 * `plan`-Feld werden dadurch korrekt aufgelöst (kein Massen-Backfill nötig).
 * Muss byte-semantisch identisch zur functions-Version `resolveEffectivePlan`
 * bleiben (§7 kanonische Fixture-Tabelle).
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`, solange noch nicht
 *   geladen). Fail-closed bei `null`/`undefined`/Nicht-Objekt.
 * @returns Der effektive Tier.
 */
export function resolveTier(sellerData: SellerLike | null | undefined): Tier {
  if (!sellerData || typeof sellerData !== 'object') {
    return 'free';
  }
  const { plan } = sellerData;
  if (typeof plan === 'string' && (VALID_TIERS as readonly string[]).includes(plan)) {
    return plan as Tier;
  }
  if (sellerData.isPremium === true) {
    return 'premium';
  }
  return 'free';
}

/**
 * Prüft, ob ein bereits aufgelöster Tier mindestens `min` erreicht (via
 * `TIER_ORDER`-Vergleich). Nimmt bewusst den bereits aufgelösten `Tier`
 * entgegen (nicht `sellerData`), damit Aufrufer `resolveTier` einmal pro
 * Render/Gate aufrufen und das Ergebnis wiederverwenden können
 * (architecture-pricing-repackaging.md §3.2, wörtliche Signatur).
 *
 * @param actual - Der aktuelle, bereits aufgelöste Tier.
 * @param min - Der geforderte Mindest-Tier.
 * @returns `true`, wenn `actual` mindestens `min` ist.
 */
export function hasMinTier(actual: Tier, min: Tier): boolean {
  return TIER_ORDER[actual] >= TIER_ORDER[min];
}

/**
 * Master-Flag für die Durchsetzung der Take-away-Gates (Mahnwesen/Vorsteuer-
 * Cockpit/Offene-Posten, PR-T5). Fail-closed: unset/nicht `'true'` ⇒ `false`
 * ⇒ IST-Verhalten (alle Take-away-Gates bleiben ungegatet). Modul-Konstante,
 * einmalig beim Bundle-Load ausgewertet (Muster wie andere FE-Flags,
 * z. B. `receiptScanAccess.ts` `RECEIPT_SCAN_FLAG`).
 */
export const TIER_V2_FLAG: boolean = process.env.REACT_APP_TIER_V2_ENABLED === 'true';

/**
 * Stabile Keys der Take-away-Features aus dem einmaligen Grandfathering-
 * Backfill (§2.3). Erweiterbar für spätere Take-aways — ausschließlich
 * Backend-befüllt (Admin-Backfill), niemals FE-geschrieben.
 */
export type GrandfatheredFeature = 'dunning' | 'vorsteuerCockpit' | 'openItems';

/**
 * Prüft, ob ein Seller für ein Feature PERMANENT grandfathered ist
 * (`sellers.grandfatheredFeatures`-Array, §2.3). KEIN Ablauf — Bestandsschutz
 * auf Dauer, ausschließlich manuell/Admin-befüllt (der neue Grace-Backfill
 * schreibt dieses Feld NICHT, §4.1). Herausgezogen aus der früheren
 * `isGrandfathered`-Logik, damit `graceWindow.ts` den permanenten Pfad ohne
 * Grace-Anteil separat prüfen kann.
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`). Fail-closed bei
 *   `null`/Nicht-Objekt.
 * @param feature - Der zu prüfende Feature-Key.
 * @returns `true`, wenn `feature` im `grandfatheredFeatures`-Array steht.
 */
export function hasPermanentGrandfathering(
  sellerData: SellerLike | null | undefined,
  feature: GrandfatheredFeature
): boolean {
  if (!sellerData || typeof sellerData !== 'object') {
    return false;
  }
  return (
    Array.isArray(sellerData.grandfatheredFeatures) &&
    sellerData.grandfatheredFeatures.includes(feature)
  );
}

/**
 * Geteilter, fail-closed Grace-Reader (§4.2): liest den ISO-8601-Ablaufzeitpunkt
 * für ein Feature aus dem rohen `grandfatheredGraceUntil`-Map-Feld und gibt ihn
 * als Millisekunden-Timestamp (`Date.parse`) zurück — oder `null`, wenn die Map
 * fehlt/kein Objekt ist, der Feature-Wert kein String ist oder nicht als endliche
 * Zeit parsebar ist (leerer String, Nicht-ISO, Timestamp-Objekt `{seconds,…}`).
 *
 * Führt bewusst KEINEN Zeitvergleich durch — der Aufrufer vergleicht strikt gegen
 * sein eigenes `nowMs` (`isGrandfathered`: `> nowMs`; `getGraceStatus`: `> nowMs`).
 * Dies ist die EINZIGE Parse-Implementierung des Grace-Reads im FE; `graceWindow.ts`
 * importiert sie (kein Copy-Paste, §5.2 / §6.1).
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`).
 * @param feature - Der zu prüfende Feature-Key.
 * @returns Ablauf-Timestamp in ms, oder `null` bei fehlenden/kaputten Daten.
 */
export function readGraceUntilMs(
  sellerData: SellerLike | null | undefined,
  feature: GrandfatheredFeature
): number | null {
  if (!sellerData || typeof sellerData !== 'object') {
    return null;
  }
  const map = sellerData.grandfatheredGraceUntil;
  if (!map || typeof map !== 'object') {
    return null;
  }
  const raw = (map as Record<string, unknown>)[feature];
  if (typeof raw !== 'string') {
    return null;
  }
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) {
    return null;
  }
  return ms;
}

/**
 * Prüft, ob ein Seller ein Feature nutzen darf, weil er entweder PERMANENT
 * grandfathered ist ODER sich in einem aktiven, befristeten Grace-Fenster
 * befindet (§5.1, Grace-Window-Neusemantik). Grandfathering/Grace gewährt IMMER
 * zusätzlich zum Tier, nimmt nie etwas weg.
 *
 * Fail-closed: kaputte/fehlende/abgelaufene Grace-Daten ⇒ nur der permanente
 * Pfad zählt. Vergleich strikt `graceUntilMs > nowMs` (der Ablauf-Moment selbst
 * gilt bereits als abgelaufen).
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`). Fail-closed bei
 *   `null`/Nicht-Objekt.
 * @param feature - Der zu prüfende Feature-Key.
 * @param nowMs - Vergleichs-Jetzt in ms (Default `Date.now()`). Injektion NUR
 *   für Tests/Banner — Call-Sites rufen weiterhin 2-argumentig auf.
 * @returns `true`, wenn `feature` permanent grandfathered ODER Grace aktiv ist.
 */
export function isGrandfathered(
  sellerData: SellerLike | null | undefined,
  feature: GrandfatheredFeature,
  nowMs: number = Date.now()
): boolean {
  if (hasPermanentGrandfathering(sellerData, feature)) {
    return true;
  }
  const graceUntilMs = readGraceUntilMs(sellerData, feature);
  return graceUntilMs !== null && graceUntilMs > nowMs;
}
